/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../../common/controller/signal-resource-list-binding.service';
import {DestroyRef, Component, Inject, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import * as _ from 'lodash';
import template from './external-demand-source-edit-page.component.html';
import {APP_SHARED} from '../../common/app-shared';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {ADMIN_LIST} from '../../common/directives/admin-list';
import {AdnSortingHeaderParam} from '../../common/directives/html/sorting-header.component';
import {CreativeApiService} from '../../creative/creative-api.service';
import {ExternalAdUnitApiService} from '../../external-ad-unit/external-ad-unit-api.service';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {
  ExternalDemandSourceApiService,
  ExternalDemandSourceModel
} from './external-demand-source-api.service';
import {AvailableCurrenciesApiService} from '../../common/controller/available-currencies-api.service';
import {TARGETING_COMPONENTS} from '../../targeting/targeting-components';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, UIRouterModule, ...ADMIN_LIST, ...TARGETING_COMPONENTS],
  selector: 'adn-external-demand-source-edit-page',
  template
})
export class ExternalDemandSourceEditPageComponent
  extends SignalAdminResourceEditPageBase<ExternalDemandSourceModel> {
  private readonly listLifetime = inject(DestroyRef);
  readonly externalPartners = ['PREBID_JS'];
  readonly extAdUnitSortParams: AdnSortingHeaderParam[] = [
    'name',
    'externalSiteId',
    'externalAdUnitId',
    'externalAdType',
    {param: 'width', label: 'dimensions'}
  ];
  readonly creativeSortParams: AdnSortingHeaderParam[] = [
    'name',
    {param: 'Layout', label: 'layout'},
    {label: 'dimensions', param: 'width'},
    {param: '', label: 'targeting'}
  ];

  private readonly currenciesState = signal<string[]>([]);
  get currencies(): string[] { return this.currenciesState(); }
  set currencies(value: string[]) { this.currenciesState.set(value); }
  canManageLayouts = false;
  usageResource: any = {
    creativeList: {},
    showExtAdUnitDetail: false,
    showCreativeDetail: false
  };
  get displayExternalAdUnits(): any[] { return this.usageResource.listState?.items() || []; }
  get hasExternalAdUnits(): boolean { return this.displayExternalAdUnits.length > 0; }
  get externalAdUnitCount(): number { return this.usageResource.listState?.totalCount() || 0; }
  get displayCreatives(): any[] { return this.usageResource.creativeList.listState?.items() || []; }
  get hasCreatives(): boolean { return this.displayCreatives.length > 0; }
  get creativeCount(): number { return this.usageResource.creativeList.listState?.totalCount() || 0; }

  private listInitialized = false;

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private externalAdUnitApi: ExternalAdUnitApiService,
    @Inject(CreativeApiService) private Creative: CreativeApiService,
    private listBinding: SignalResourceListBindingService,
    private availableCurrenciesApi: AvailableCurrenciesApiService
  ) {
    super(inject(ExternalDemandSourceApiService), 'externalDemandSource');
    this.canManageLayouts = LocalUserPermissions.getAllVisPermissions()?.MANAGE_LAYOUTS === true;
    this.availableCurrenciesApi.get().subscribe((results) => {
      this.currencies = _.map(results, 'currency');
    });
  }

  protected override initialiseModel(model: ExternalDemandSourceModel): ExternalDemandSourceModel {
    return {
      ...super.initialiseModel(model),
      externalPartner: model.externalPartner || 'PREBID_JS'
    };
  }

  protected override afterEditingModelReset(): void {
    if (this.editingModel.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {externalDemandSource: this.editingModel.id};
      this.usageResource = this.listBinding.create(this.externalAdUnitApi, 'fullExternalAdUnitsForExternalDemandSource', this.listLifetime, {
      baseTable: 'externalAdUnit',
        moreParams,
        noInitQuery: true,
      }, this.usageResource);
      this.usageResource.creativeList = this.listBinding.create(this.Creative, 'fullCreativesForExternalDemandSource', this.listLifetime, {
      baseTable: 'creative',
        moreParams,
        noInitQuery: true
      }, this.usageResource.creativeList);
      if (!this.isNew) {
        this.usageResource.makeQuery?.();
        this.usageResource.creativeList.makeQuery?.();
      }
    }
  }

  protected override afterSave(): void {
    if (!this.isNew) {
      this.usageResource.makeQuery?.();
      this.usageResource.creativeList.makeQuery?.();
    }
  }
}
